import type { Citation, RunError } from '@/api/runs';
import {
  isActivePhase,
  type RunState,
  type StageDetail,
  type StopState,
} from '../runs/runStore';
import type { ConvMessage } from '../types';

/**
 * What an answer shows, from its run in the store (while it streams, and
 * for the rest of the session) or from the stored message (history).
 */

export type AnswerStatus =
  | {
      kind: 'active';
      phase: 'queued' | 'running' | 'stopping';
      stage: string | null;
      detail: StageDetail | null;
      stop: StopState;
      reconnecting: boolean;
      startedAt: number | null;
      hasText: boolean;
    }
  | { kind: 'completed'; durationMs: number | null }
  /** A clarification waiting for this user's reply. */
  | { kind: 'awaiting_reply' }
  /** A clarification that was closed, or that someone else was asked. */
  | { kind: 'clarified' }
  | { kind: 'stopped' }
  | { kind: 'failed'; error: RunError | null; detail: string | null }
  /** `stillRunning`: the server still had it active; Stop cancels it. */
  | { kind: 'disconnected'; stillRunning: boolean; stop: StopState }
  | { kind: 'interrupted'; stillRunning: boolean; stop: StopState }
  /** An answer from before chat runs. */
  | { kind: 'legacy' };

export interface AnswerView {
  status: AnswerStatus;
  /** The text to render (may be empty). */
  text: string;
  /** Text still arriving. */
  streaming: boolean;
  /** Text cut short: an incomplete end is held back. */
  partial: boolean;
  /** Shown with the "Partial answer" label. */
  partialLabel: boolean;
  citations: Citation[] | null;
  /** Rendered as legacy text (format null). */
  legacy: boolean;
  /** A clarification: a question from the assistant, not an answer. */
  clarification: boolean;
}

const ACTIVE_STATUSES = new Set(['queued', 'running', 'stopping']);

/**
 * A clarification. Its run stays `awaiting_reply` after it closes, so the
 * message's clarification status decides. `pendingHere` is the Q&A's
 * pending_clarification pointing at this message; until that is read back,
 * the user's own run that just asked counts too. While another run is active
 * (`busy`, the reply being answered) nothing waits for the user.
 */
const clarificationView = (
  message: ConvMessage,
  run: RunState | undefined,
  pendingHere: boolean,
  busy: boolean,
): AnswerView => {
  const open =
    (message.clarification?.status ?? 'awaiting_reply') === 'awaiting_reply';
  const askedHere = !!run?.own && run.phase === 'awaiting_reply';
  const waiting = open && !busy && (pendingHere || askedHere);
  return {
    status: { kind: waiting ? 'awaiting_reply' : 'clarified' },
    text: message.response || run?.text || '',
    streaming: false,
    partial: false,
    partialLabel: false,
    citations: null,
    legacy: false,
    clarification: true,
  };
};

export const answerView = (
  message: ConvMessage,
  run: RunState | undefined,
  pendingHere = false,
  busy = false,
): AnswerView => {
  if (
    message.message_type === 'clarification' ||
    run?.phase === 'awaiting_reply'
  ) {
    return clarificationView(message, run, pendingHere, busy);
  }
  const clarification = false;
  const legacy =
    message.format === null ||
    (message.format === undefined && !message.run_id && !message.local);
  const citations = run?.citations ?? message.citations ?? null;

  if (run) {
    const text = run.text;
    if (isActivePhase(run.phase)) {
      return {
        status: {
          kind: 'active',
          phase: run.phase as 'queued' | 'running' | 'stopping',
          stage: run.stage,
          detail: run.detail,
          stop: run.stop,
          reconnecting: run.reconnecting,
          startedAt: run.startedAt,
          hasText: text.length > 0,
        },
        text,
        streaming: run.phase !== 'stopping',
        partial: true,
        partialLabel: false,
        citations,
        legacy: false,
        clarification,
      };
    }
    switch (run.phase) {
      case 'completed':
        return {
          status: { kind: 'completed', durationMs: run.durationMs },
          // The stored answer once loaded (a stop that lost the race
          // stored the whole answer); the streamed text until then.
          text: message.response || text,
          streaming: false,
          // Until it is loaded, the text frozen by Stop may end mid-tag.
          partial: !message.response,
          partialLabel: false,
          citations,
          legacy: false,
          clarification,
        };
      case 'stopped':
      case 'disconnected':
      case 'interrupted': {
        // A Stop pressed while the text was arriving froze it on screen.
        // Otherwise (stopped elsewhere, or a Stop on a lost run or before
        // its pick-up delivered anything) the stored answer is shown once
        // the list is loaded again.
        const shown = run.textFrozen ? text : message.response || text;
        return {
          status:
            run.phase === 'stopped'
              ? { kind: 'stopped' }
              : {
                  kind: run.phase,
                  stillRunning: run.stillRunning,
                  stop: run.stop,
                },
          text: shown,
          streaming: false,
          partial: true,
          partialLabel: shown.length > 0,
          citations,
          legacy: false,
          clarification,
        };
      }
      default:
        return {
          status: { kind: 'failed', error: run.error, detail: null },
          text,
          streaming: false,
          partial: true,
          partialLabel: text.length > 0,
          citations,
          legacy: false,
          clarification,
        };
    }
  }

  const status = message.run_status ?? null;
  if (status && ACTIVE_STATUSES.has(status)) {
    return {
      status: {
        kind: 'active',
        phase: status as 'queued' | 'running' | 'stopping',
        stage: null,
        detail: null,
        stop: 'none',
        reconnecting: false,
        startedAt: null,
        hasText: false,
      },
      text: '',
      streaming: true,
      partial: true,
      partialLabel: false,
      citations,
      legacy: false,
      clarification,
    };
  }
  switch (status) {
    case 'completed':
      return {
        status: { kind: 'completed', durationMs: null },
        text: message.response ?? '',
        streaming: false,
        partial: false,
        partialLabel: false,
        citations,
        legacy,
        clarification,
      };
    case 'stopped':
      return {
        status: { kind: 'stopped' },
        text: message.response ?? '',
        streaming: false,
        partial: true,
        partialLabel: !!message.response,
        citations,
        legacy,
        clarification,
      };
    case 'failed':
      // The stored answer of a failed run is the generic error text.
      return {
        status: {
          kind: 'failed',
          error: null,
          detail: message.response || null,
        },
        text: '',
        streaming: false,
        partial: false,
        partialLabel: false,
        citations: null,
        legacy,
        clarification,
      };
    default:
      return {
        status: { kind: 'legacy' },
        text: message.response ?? '',
        streaming: false,
        partial: false,
        partialLabel: false,
        citations,
        legacy,
        clarification,
      };
  }
};

/** The resubmission actions an answer offers (spec §7). */
export const answerActions = (message: ConvMessage, view: AnswerView) => {
  const kind = view.status.kind;
  const stored = !message.local || message.local === 'pending';
  const rejected =
    kind === 'failed' && view.status.kind === 'failed'
      ? view.status.error?.code === 'rejected' || message.local === 'removed'
      : false;
  return {
    stop:
      kind === 'active' ||
      ((view.status.kind === 'disconnected' ||
        view.status.kind === 'interrupted') &&
        view.status.stillRunning),
    retry:
      stored &&
      !rejected &&
      (kind === 'stopped' ||
        kind === 'failed' ||
        (kind === 'legacy' && !message.response)),
    alternative:
      stored &&
      (kind === 'completed' || (kind === 'legacy' && !!message.response)),
    reconnect: kind === 'disconnected' || kind === 'interrupted',
    // No Copy or CSV on a clarification (addendum §3.3).
    hasBody: !view.clarification && view.text.trim().length > 0,
    skipClarification: stored && kind === 'awaiting_reply',
  };
};
